import * as React from 'react';
import { z } from 'zod/v4';
import DynamicForm from '../custom/dynamic-form';
import { Button } from '../core/button';
import { Input } from '../core/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../core/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '../core/command';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../core/select';
import { Switch } from '../core/switch';
import { MultipleSelector, type Option } from '../core/multiple-selector';
import type { ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import { Plus, Trash2, Search, X, Loader2 } from 'lucide-react';

// Stable reference to avoid re-creating default arrays on every render,
// which would cascade through useMemo deps and trigger a form reset.
const EMPTY_OPTIONS: Option[] = [];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FeedbackCampaignFormQuestion = {
  questionText: string;
  questionType: 'rating' | 'text';
  order: number;
};

/**
 * Values emitted by the form on submit.
 * Note: `status` has been removed -- it is now managed via a toggle on the
 * list page.  We keep `groupIds` as `string[]` in the output so the
 * consuming routes don't need to change their mutation payloads.
 */
export type FeedbackCampaignFormValues = {
  title: string;
  description: string;
  campaignType: 'general' | 'event-linked';
  targetType: 'all' | 'groups' | 'users';
  isMandatory: boolean;
  eventId?: string | null;
  questions: FeedbackCampaignFormQuestion[];
  groupIds: string[];
  userIds: string[];
};

export type EventSearchResult = {
  eventId: string;
  title: string;
  startTime: string | Date;
  className?: string | null;
};

export type FeedbackCampaignFormProps = {
  defaultValues?: Partial<FeedbackCampaignFormValues>;
  onSubmit: (values: FeedbackCampaignFormValues) => void;
  onClose: () => void;
  isSubmitting?: boolean;
  /** Async search callback — EventSearchField manages its own loading/results state. */
  onEventSearch?: (query: string) => Promise<EventSearchResult[]>;
  /** @deprecated Use async onEventSearch instead. Kept for back-compat. */
  eventSearchResults?: EventSearchResult[];
  /** @deprecated Use async onEventSearch instead. Kept for back-compat. */
  isSearchingEvents?: boolean;
  /** Async search for groups (3+ chars, debounced by MultipleSelector). */
  onSearchGroups?: (query: string) => Promise<Option[]>;
  /** Async search for users (3+ chars, debounced by MultipleSelector). */
  onSearchUsers?: (query: string) => Promise<Option[]>;
  /** Pre-selected group options for edit mode (displayed as badges on load). */
  defaultSelectedGroups?: Option[];
  /** Pre-selected user options for edit mode (displayed as badges on load). */
  defaultSelectedUsers?: Option[];
  /** Pre-selected event for edit mode (displayed as a chip on load). */
  defaultSelectedEvent?: EventSearchResult | null;
  labels?: {
    title?: string;
    description?: string;
    campaignType?: string;
    targetType?: string;
    eventId?: string;
    questions?: string;
    addQuestion?: string;
    questionPlaceholder?: string;
    templateHint?: string;
    submit?: string;
    cancel?: string;
    submitting?: string;
    isMandatory?: string;
    recurringExplanation?: string;
    eventSearchPlaceholder?: string;
    eventSearchEmpty?: string;
    eventSearchHint?: string;
    clearEvent?: string;
    groupSearchPlaceholder?: string;
    userSearchPlaceholder?: string;
  };
};

// ---------------------------------------------------------------------------
// Zod schema  (used internally by DynamicForm)
// ---------------------------------------------------------------------------

const feedbackCampaignSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional().default(''),
  campaignType: z.enum(['general', 'event-linked']).default('general'),
  targetType: z.enum(['all', 'groups', 'users']).default('all'),
  isMandatory: z.boolean().default(false),
  eventId: z.string().optional(),
  // DynamicForm treats arrays as "relation-many" by default, but we use
  // customRenderers for questions so the schema type doesn't matter much --
  // we just need it to be an array for validation.
  questions: z
    .array(
      z.object({
        questionText: z.string(),
        questionType: z.enum(['rating', 'text']).default('rating'),
        order: z.number(),
      }),
    )
    .default([{ questionText: '', questionType: 'rating' as const, order: 0 }]),
  // groupIds and userIds are stored as Option[] inside the form
  // (MultipleSelector) and converted back to string[] on submit.
  groupIds: z.array(z.any()).default([]),
  userIds: z.array(z.any()).default([]),
});

type SchemaValues = z.infer<typeof feedbackCampaignSchema>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatEventDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(d);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AsyncMultiSelect({
  field,
  onSearch,
  placeholder,
  emptyLabel,
}: {
  field: ControllerRenderProps<SchemaValues>;
  onSearch?: (query: string) => Promise<Option[]>;
  placeholder: string;
  emptyLabel: string;
}) {
  const selected = (field.value ?? []) as Option[];
  return (
    <MultipleSelector
      value={selected}
      onChange={(options) => field.onChange(options)}
      onSearch={onSearch}
      delay={400}
      placeholder={placeholder}
      emptyIndicator={
        <p className="text-center text-sm text-muted-foreground">
          {emptyLabel}
        </p>
      }
      loadingIndicator={
        <div className="flex items-center justify-center py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      }
      hidePlaceholderWhenSelected
    />
  );
}

function QuestionsEditor({
  questions,
  onChange,
  labels,
}: {
  questions: FeedbackCampaignFormQuestion[];
  onChange: (next: FeedbackCampaignFormQuestion[]) => void;
  labels?: FeedbackCampaignFormProps['labels'];
}) {
  const update = (
    updater:
      | FeedbackCampaignFormQuestion[]
      | ((prev: FeedbackCampaignFormQuestion[]) => FeedbackCampaignFormQuestion[]),
  ) => {
    onChange(typeof updater === 'function' ? updater(questions) : updater);
  };

  const addQuestion = () => {
    update((prev) => [
      ...prev,
      { questionText: '', questionType: 'rating' as const, order: prev.length },
    ]);
  };

  const removeQuestion = (index: number) => {
    update((prev) =>
      prev.filter((_, i) => i !== index).map((q, i) => ({ ...q, order: i })),
    );
  };

  const changeText = (index: number, text: string) => {
    update((prev) =>
      prev.map((q, i) => (i === index ? { ...q, questionText: text } : q)),
    );
  };

  const changeType = (index: number, type: 'rating' | 'text') => {
    update((prev) =>
      prev.map((q, i) => (i === index ? { ...q, questionType: type } : q)),
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span />
        <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          {labels?.addQuestion ?? 'Add question'}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {labels?.templateHint ??
          'Use {{teacherName}}, {{className}}, or {{eventTitle}} as template variables.'}
      </p>
      {questions.map((question, index) => (
        <div key={index} className="flex items-start gap-2">
          <span className="mt-2.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
            {index + 1}
          </span>
          <Input
            value={question.questionText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              changeText(index, e.target.value)
            }
            placeholder={labels?.questionPlaceholder ?? 'Enter question...'}
            className="flex-1"
          />
          <Select
            value={question.questionType}
            onValueChange={(v) => changeType(index, v as 'rating' | 'text')}
          >
            <SelectTrigger className="w-[110px] shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rating">Star</SelectItem>
              <SelectItem value="text">Text</SelectItem>
            </SelectContent>
          </Select>
          {questions.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeQuestion(index)}
              className="shrink-0"
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

function EventSearchField({
  currentEventId,
  onSelect,
  onClear,
  onEventSearch,
  labels,
}: {
  currentEventId: string | undefined;
  onSelect: (result: EventSearchResult) => void;
  onClear: () => void;
  onEventSearch?: (query: string) => Promise<EventSearchResult[]>;
  labels?: FeedbackCampaignFormProps['labels'];
}) {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [selectedLabel, setSelectedLabel] = React.useState('');
  const [results, setResults] = React.useState<EventSearchResult[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchIdRef = React.useRef(0);

  const handleSearchChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length >= 3 && onEventSearch) {
      setIsSearching(true);
      debounceRef.current = setTimeout(() => {
        const id = ++searchIdRef.current;
        onEventSearch(value).then((data) => {
          if (id === searchIdRef.current) {
            setResults(data);
            setIsSearching(false);
          }
        }).catch(() => {
          if (id === searchIdRef.current) {
            setIsSearching(false);
          }
        });
      }, 400);
    } else {
      setResults([]);
      setIsSearching(false);
    }
  };

  React.useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const handleSelect = (result: EventSearchResult) => {
    const label = `${result.title} - ${formatEventDate(result.startTime)}${result.className ? ` - ${result.className}` : ''}`;
    setSelectedLabel(label);
    setOpen(false);
    setQuery('');
    onSelect(result);
  };

  if (currentEventId && selectedLabel) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm">
        <span className="flex-1 truncate">{selectedLabel}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClear}
          className="h-auto w-auto shrink-0 rounded-sm p-0 opacity-70 hover:bg-transparent hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-10 w-full justify-between px-3 py-2 font-normal hover:bg-background"
        >
          <span className="text-muted-foreground">
            {labels?.eventSearchPlaceholder ?? 'Search for an event...'}
          </span>
          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              className="h-11 rounded-md border-0 bg-transparent px-0 py-3 shadow-none focus-visible:ring-0"
              placeholder={labels?.eventSearchPlaceholder ?? 'Type at least 3 characters...'}
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleSearchChange(e.target.value)
              }
            />
            {isSearching && (
              <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
            )}
          </div>
          <CommandList>
            {query.length < 3 ? (
              <p className="py-4 text-center text-xs text-muted-foreground">
                {labels?.eventSearchHint ?? 'Type at least 3 characters to search'}
              </p>
            ) : results.length === 0 && !isSearching ? (
              <CommandEmpty>
                {labels?.eventSearchEmpty ?? 'No events found'}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {results.map((result) => (
                  <CommandItem
                    key={result.eventId}
                    value={result.eventId}
                    onSelect={() => handleSelect(result)}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{result.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatEventDate(result.startTime)}
                        {result.className ? ` \u00B7 ${result.className}` : ''}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FeedbackCampaignForm({
  defaultValues,
  onSubmit,
  onClose,
  isSubmitting = false,
  onEventSearch,
  eventSearchResults = [],
  isSearchingEvents = false,
  onSearchGroups,
  onSearchUsers,
  defaultSelectedGroups = EMPTY_OPTIONS,
  defaultSelectedUsers = EMPTY_OPTIONS,
  defaultSelectedEvent,
  labels,
}: FeedbackCampaignFormProps) {
  const formRef = React.useRef<HTMLFormElement>(null);

  // -- Compute DynamicForm defaultValues ------------------------------------
  const dynamicDefaultValues = React.useMemo<Partial<SchemaValues>>(() => {
    if (!defaultValues) return { questions: [{ questionText: '', questionType: 'rating' as const, order: 0 }] };

    return {
      title: defaultValues.title ?? '',
      description: defaultValues.description ?? '',
      campaignType: defaultValues.campaignType ?? 'general',
      targetType: defaultValues.targetType ?? 'all',
      isMandatory: defaultValues.isMandatory ?? false,
      eventId: defaultValues.eventId ?? undefined,
      questions:
        defaultValues.questions && defaultValues.questions.length > 0
          ? defaultValues.questions
          : [{ questionText: '', questionType: 'rating' as const, order: 0 }],
      groupIds: defaultSelectedGroups as any,
      userIds: defaultSelectedUsers as any,
    };
  }, [defaultValues, defaultSelectedGroups, defaultSelectedUsers]);

  // -- Handle submit: convert Option[] back to string[] ---------------------
  const handleSubmit = async (data: SchemaValues) => {
    const groupIds = (data.groupIds as unknown as Option[])
      .map((o) => o.value)
      .filter(Boolean);
    const userIds = (data.userIds as unknown as Option[])
      .map((o) => o.value)
      .filter(Boolean);

    const isEventLinked = data.campaignType === 'event-linked';
    onSubmit({
      title: data.title,
      description: data.description ?? '',
      campaignType: data.campaignType,
      targetType: isEventLinked ? 'all' : data.targetType,
      isMandatory: data.isMandatory ?? false,
      eventId: isEventLinked && data.eventId ? data.eventId : undefined,
      questions: (data.questions ?? []).filter(
        (q) => q.questionText.trim() !== '',
      ),
      groupIds: isEventLinked ? [] : (data.targetType === 'groups' ? groupIds : []),
      userIds: isEventLinked ? [] : (data.targetType === 'users' ? userIds : []),
    });
  };

  // -- Custom renderers -----------------------------------------------------

  const customRenderers: Record<
    string,
    (context: {
      field: ControllerRenderProps<SchemaValues>;
      error?: string;
      methods: UseFormReturn<SchemaValues>;
    }) => React.ReactNode
  > = {
    // --- Campaign type select ---
    campaignType: ({ field }) => (
      <Select
        value={field.value as string}
        onValueChange={(v) => field.onChange(v)}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="general">General</SelectItem>
          <SelectItem value="event-linked">After event</SelectItem>
        </SelectContent>
      </Select>
    ),

    // --- Target type select ---
    targetType: ({ field }) => (
      <Select
        value={field.value as string}
        onValueChange={(v) => field.onChange(v)}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All students</SelectItem>
          <SelectItem value="groups">Specific groups</SelectItem>
          <SelectItem value="users">Specific users</SelectItem>
        </SelectContent>
      </Select>
    ),

    // --- Event search autocomplete ---
    eventId: ({ field, methods }) => {
      const campaignType = methods.watch('campaignType');
      const currentEventId = field.value as string | undefined;
      const isRecurring = campaignType === 'event-linked' && !currentEventId;

      return (
        <div className="space-y-2">
          <EventSearchField
            currentEventId={currentEventId}
            onSelect={(result) => field.onChange(result.eventId)}
            onClear={() => field.onChange('')}
            onEventSearch={onEventSearch}
            labels={labels}
          />
          {isRecurring && (
            <p className="text-xs text-muted-foreground">
              {labels?.recurringExplanation ??
                'This campaign will trigger after every event where the student was present (based on scorecard).'}
            </p>
          )}
        </div>
      );
    },

    // --- Groups multi-select with async search ---
    groupIds: ({ field }) => (
      <AsyncMultiSelect
        field={field}
        onSearch={onSearchGroups}
        placeholder={labels?.groupSearchPlaceholder ?? 'Search groups (3+ chars)...'}
        emptyLabel="No groups found"
      />
    ),

    // --- Users multi-select with async search ---
    userIds: ({ field }) => (
      <AsyncMultiSelect
        field={field}
        onSearch={onSearchUsers}
        placeholder={labels?.userSearchPlaceholder ?? 'Search students (3+ chars)...'}
        emptyLabel="No students found"
      />
    ),

    // --- Mandatory toggle ---
    isMandatory: ({ field }) => (
      <div className="flex items-center gap-3">
        <Switch
          checked={field.value as boolean}
          onCheckedChange={(checked) => field.onChange(checked)}
        />
        <span className="text-sm text-muted-foreground">
          {field.value ? 'Students must complete all questions before closing' : 'Students can dismiss without answering'}
        </span>
      </div>
    ),

    // --- Questions list ---
    questions: ({ field }) => {
      const current = (field.value ?? [
        { questionText: '', questionType: 'rating' as const, order: 0 },
      ]) as FeedbackCampaignFormQuestion[];

      return (
        <QuestionsEditor
          questions={current}
          onChange={(next) => field.onChange(next)}
          labels={labels}
        />
      );
    },
  };

  // -- Field visibility -----------------------------------------------------

  const fieldVisibility = React.useCallback(
    ({
      key,
      values,
    }: {
      key: string;
      values: SchemaValues;
      isEditing: boolean;
    }): boolean => {
      if (key === 'eventId') return values.campaignType === 'event-linked';
      if (key === 'targetType') return values.campaignType !== 'event-linked';
      if (key === 'groupIds') return values.campaignType !== 'event-linked' && values.targetType === 'groups';
      if (key === 'userIds') return values.campaignType !== 'event-linked' && values.targetType === 'users';
      return true;
    },
    [],
  );

  // -- Label mappings -------------------------------------------------------

  const labelMappings: Record<string, string> = {
    title: labels?.title ?? 'Title',
    description: labels?.description ?? 'Description',
    campaignType: labels?.campaignType ?? 'Type',
    targetType: labels?.targetType ?? 'Target audience',
    isMandatory: labels?.isMandatory ?? 'Mandatory',
    eventId: labels?.eventId ?? 'Linked event (optional for recurring)',
    questions: labels?.questions ?? 'Questions',
    groupIds: 'Groups',
    userIds: 'Students',
  };

  // -- Render ---------------------------------------------------------------

  return (
    <DynamicForm
      schema={feedbackCampaignSchema}
      onSubmit={handleSubmit}
      onClose={onClose}
      defaultValues={dynamicDefaultValues}
      isEditing={!!defaultValues?.title}
      formRef={formRef}
      labelMappings={labelMappings}
      fieldVisibility={fieldVisibility}
      customRenderers={customRenderers as any}
    />
  );
}
